import React from "react";
import { useQuery } from "react-query";
import { i18n } from "i18n";
import axios from "axios";
import { handleEnterPress } from "component-library";
import {
  AddOnEntitlements,
  SocialMediaPlatformCode,
  UserTypes,
} from "shared-types";
import {
  checkIsYp,
  doesUserHaveEntitlements,
  getUserAttribute,
  UserAttributes,
} from "shared-auth";
import { getFilmmakersSSOLink } from "shared-utils";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { config } from "config";
import { SocialMedia } from "models/Social";
import { IAccessibilityContent } from "pages/MainPage/MainPage";
import { ReactComponent as OpenInNewTabIcon } from "component-library/src/images/svg/icons/open-in-new-tab.svg";
import SpotlightPerformerWebsiteLogoLink from "./SpotlightPerformerWebsiteLogoLink";
import FilmmakersProfileWebsiteLogoLink from "./FilmmakersProfileWebsiteLogoLink";
import InstagramIntegration from "./InstagramIntegration";
import { socialMediaQueryKey } from "pages/MainPage/components/SocialMediaList/constants";
import "./SocialMediaList.scss";

export interface ISocialMediaListContent extends IAccessibilityContent {
  webSiteLabel: string;
}

export interface ISocialMediaListProps {
  content: ISocialMediaListContent;
  socialMedia?: SocialMedia;
  editSocialMedia: () => void;
  isEditAvailable: boolean;
  isFilmmakersProfile?: boolean;
  isSLProfileInFilmmakers?: boolean;
  profileId?: string;
  isAgent?: boolean;
  isReadonly?: boolean;
  userType: UserTypes;
}

export const SocialMediaList: React.FC<
  React.PropsWithChildren<ISocialMediaListProps>
> = React.memo(
  ({
    socialMedia = {},
    editSocialMedia,
    content,
    isEditAvailable,
    isFilmmakersProfile,
    isSLProfileInFilmmakers,
    profileId,
    isAgent,
    isReadonly,
    userType,
  }) => {
    const profileIdAttr =
      profileId || getUserAttribute(UserAttributes.MasterPortalGuid);
    const { website = "" } = socialMedia;
    const isInstagramIntegrationEnabled = useLaunchDarklyFlags(
      FEATURE_FLAGS.InstagramIntegration
    );

    const isYoungPerformer = checkIsYp(userType);

    const { data = {} } = useQuery(
      [socialMediaQueryKey, isInstagramIntegrationEnabled, profileIdAttr],
      {
        queryFn: async () => {
          const { data } = await axios(
            `${config.profileApiUrl}/profiles/${profileIdAttr}/social/accounts`
          );

          return data.accounts.reduce((acc, res) => {
            return { ...acc, [res.platformCode]: res };
          }, {});
        },
        enabled: isInstagramIntegrationEnabled,
      }
    );

    const userHasFilmmakersEntitlement = isAgent
      ? false
      : doesUserHaveEntitlements(AddOnEntitlements.FilmmakersSync);

    const shouldShowFilmmakersSsoLink =
      userHasFilmmakersEntitlement && !isReadonly;

    const handleRedirectToFilmmakers = async () => {
      const data = await getFilmmakersSSOLink(
        "https://www.filmmakers.eu/sso/spotlight/inbound"
      );
      window.open(data.url, "_blank");
    };

    const getWebsiteIcon = () => {
      // If profile is imported from Filmmakers, show Filmmakers logo
      if (isFilmmakersProfile && website) {
        return <FilmmakersProfileWebsiteLogoLink href={website} tabIndex={0} />;
      }

      // If Spotlight performer and has FM entitlement, show both Filmmakers and Spotlight logos
      if (userHasFilmmakersEntitlement || isSLProfileInFilmmakers) {
        return (
          <div className="flex items-center">
            {website && (
              <SpotlightPerformerWebsiteLogoLink
                href={website}
                webSiteLabel={content.webSiteLabel}
              />
            )}
            <div className="mb-1 flex flex-col">
              <FilmmakersProfileWebsiteLogoLink
                href={`https://www.filmmakers.eu/spotlight-redirect/${profileIdAttr}`}
                tabIndex={website ? 1 : 0}
              />
              {shouldShowFilmmakersSsoLink && (
                <button
                  className="flex items-center space-x-1 text-xs text-color-brand-one hover:underline"
                  onClick={handleRedirectToFilmmakers}
                >
                  <span>
                    {i18n.t(
                      "performer:profile.socialMediaList.filmmakersLogIn"
                    )}
                  </span>
                  <OpenInNewTabIcon
                    width={15}
                    height={15}
                    className="text-color-brand-one"
                  />
                </button>
              )}
            </div>
          </div>
        );
      }

      // If Spotlight performer and no FM entitlement, show only Spotlight logo
      return (
        (website || isEditAvailable) && (
          <SpotlightPerformerWebsiteLogoLink
            href={website}
            webSiteLabel={content.webSiteLabel}
          />
        )
      );
    };

    const instagramData = data[SocialMediaPlatformCode.Instagram] || {};

    return (
      <div className="c-social-media-list">
        <div className="c-social-media-list__item">{getWebsiteIcon()}</div>
        {isEditAvailable && (
          <a
            className="c-social-media-list__edit icon-edit"
            onKeyPress={handleEnterPress(editSocialMedia)}
            aria-label={`${content.editLinkAltText} ${content.webSiteLabel}`}
            onClick={editSocialMedia}
            tabIndex={0}
          />
        )}

        {isInstagramIntegrationEnabled && !isYoungPerformer && (
          <InstagramIntegration
            isReadonly={isReadonly}
            status={instagramData.status}
            instagramProfileUrl={instagramData.profileUrl}
            instagramAccountId={instagramData.id}
            profileId={profileIdAttr}
          />
        )}
      </div>
    );
  }
);
